import React, { useEffect, useState } from 'react';

import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  Chip,
  Pagination,
  CircularProgress
} from '@mui/material';

import {
  Delete,
  Edit,
  Send
} from '@mui/icons-material';

import GridLayout from '../components/layout/GridLayout';
import Menu from '../components/layout/Menu';
import Card from '../components/ui/Card';

import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

interface Post {
  id: number;
  content: string;
  user_email: string;
  user_id: string;
  created_at: string;
}

interface Props {
  isMenuOpen?: boolean;
}

const POSTS_PER_PAGE = 10;

export default function CommunityPage({
  isMenuOpen = false
}: Props) {

  const { user } = useAuth();

  const [posts, setPosts] = useState<Post[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);

  const [page, setPage] = useState(1);

  // =========================
  // LOAD POSTS
  // =========================

  const fetchPosts = async () => {

    setLoading(true);

    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setPosts(data);
    }

    setLoading(false);
  };

  useEffect(() => {

    fetchPosts();

    const channel = supabase
      .channel('community-live')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'posts'
        },
        () => {
          fetchPosts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };

  }, []);

  // =========================
  // SUBMIT
  // =========================

  const handleSubmit = async () => {

    if (!inputText.trim()) return;

    if (!user) {
      alert('Please login first');
      return;
    }

    // EDIT POST
    if (editingId) {

      const { error } = await supabase
        .from('posts')
        .update({
          content: inputText
        })
        .eq('id', editingId);

      if (!error) {
        setEditingId(null);
        setInputText('');
        fetchPosts();
      }

      return;
    }

    // NEW POST
    const { error } = await supabase
      .from('posts')
      .insert([
        {
          content: inputText,
          user_email: user.email,
          user_id: user.id
        }
      ]);

    if (!error) {
      setInputText('');
      fetchPosts();
    } else {
      console.error(error);
      alert(error.message);
    }
  };

  // =========================
  // DELETE
  // =========================

  const handleDelete = async (id: number) => {

    const ok = window.confirm('Delete this post?');

    if (!ok) return;

    const { error } = await supabase
      .from('posts')
      .delete()
      .eq('id', id);

    if (!error) {
      fetchPosts();
    }
  };

  // =========================
  // EDIT
  // =========================

  const handleEdit = (post: Post) => {
    setEditingId(post.id);
    setInputText(post.content);
  };

  // =========================
  // PAGINATION
  // =========================

  const totalPages = Math.ceil(
    posts.length / POSTS_PER_PAGE
  );

  const paginatedPosts = posts.slice(
    (page - 1) * POSTS_PER_PAGE,
    page * POSTS_PER_PAGE
  );

  return (

    <GridLayout
      isMenuOpen={isMenuOpen}

      left={<Menu />}

      main={

        <Stack spacing={3}>

          {/* TITLE */}

          <Card>

            <Typography
              variant="h4"
              sx={{
                color: '#34de9aab',
                fontWeight: 'bold'
              }}
            >
              Talk Talk Talk
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'gray',
                mt: 1
              }}
            >
              Pilates Community Board
            </Typography>

          </Card>

          {/* WRITE POST */}

          <Card>

            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: '#34de9aab'
              }}
            >
              {editingId
                ? 'Edit your post'
                : 'Write something'}
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              value={inputText}
              onChange={(e) =>
                setInputText(e.target.value)
              }
              placeholder="Share your Pilates journey..."
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                  borderColor: '#34de9aab'
                }
              }}
            />

            <Stack
              direction="row"
              spacing={2}
              sx={{
                mt: 2,
                alignItems: 'center'
              }}
            >

              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={handleSubmit}
                sx={{
                  bgcolor: '#34de9aab',
                  '&:hover': {
                    bgcolor: '#2bc48a'
                  }
                }}
              >
                {editingId ? 'Update' : 'Post'}
              </Button>

              {editingId && (

                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingId(null);
                    setInputText('');
                  }}
                >
                  Cancel
                </Button>

              )}

            </Stack>

          </Card>

          {/* POSTS */}

          <Card>

            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: '#34de9aab'
              }}
            >
              Community Feed
            </Typography>

            {loading && (

              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress />
              </Box>

            )}

            <Stack spacing={3}>

              {paginatedPosts.map((post) => {

                const isMine =
                  user?.id === post.user_id;

                return (

                  <Box key={post.id}>

                    {/* HEADER */}

                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >

                      {/* USER */}

                      <Box>

                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            color: '#34de9aab'
                          }}
                        >
                          {post.user_email}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: 'gray'
                          }}
                        >
                          {new Date(
                            post.created_at
                          ).toLocaleString()}
                        </Typography>

                      </Box>

                      {/* ACTIONS */}

                      {isMine && (

                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            alignItems: 'center'
                          }}
                        >

                          <Button
                            size="small"
                            startIcon={<Edit />}
                            onClick={() =>
                              handleEdit(post)
                            }
                          >
                            Edit
                          </Button>

                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() =>
                              handleDelete(post.id)
                            }
                          >
                            Delete
                          </Button>

                        </Stack>

                      )}

                    </Stack>

                    {/* CONTENT */}

                    <Typography
                      sx={{
                        mt: 2,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.7
                      }}
                    >
                      {post.content}
                    </Typography>

                    <Divider sx={{ mt: 3 }} />

                  </Box>

                );
              })}

            </Stack>

            {/* PAGINATION */}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4
              }}
            >

              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) =>
                  setPage(value)
                }
                color="secondary"
              />

            </Box>

          </Card>

        </Stack>
      }

      right={

        <Stack spacing={3}>

          {/* TRENDING */}

          <Card>

            <Typography
              variant="h6"
              sx={{
                color: '#34de9aab',
                mb: 2
              }}
            >
              Trending
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >

              <Chip label="#Pilates" />
              <Chip label="#Rehabilitation" />
              <Chip label="#Routine" />
              <Chip label="#Posture" />
              <Chip label="#NeckCare" />
              <Chip label="#IoT" />

            </Box>

          </Card>

          {/* RECENT */}

          <Card>

            <Typography
              variant="h6"
              sx={{
                color: '#34de9aab',
                mb: 2
              }}
            >
              Recent Posts
            </Typography>

            <Stack spacing={2}>

              {posts.slice(0, 5).map((post) => (

                <Box key={post.id}>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#34de9aab'
                    }}
                  >
                    {post.user_email}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {post.content}
                  </Typography>

                </Box>

              ))}

            </Stack>

          </Card>

        </Stack>
      }
    />
  );
}